import { useCallback, useContext, useMemo } from 'react';
import { AccessContext } from '../store/access/AccessContext';
import { hasPermission } from '../utils/role-based-access/hasPermission';
import { PermissionCode } from '../constants/permissionCodes';

export interface IAccessControl {
  can: (permission: PermissionCode) => boolean;
  canAny: (permissions: PermissionCode[]) => boolean;
  rolePermissions: string[];
  isReady: boolean;
}

export const useAccessControl = (): IAccessControl => {
  const access = useContext(AccessContext);
  const isReady = access?.status === 'ready';
  const isUngated = !!access && access.menuGuardEnabled === false;

  const rolePermissions = useMemo(
    () => (isReady ? access?.rolePermissions || [] : []),
    [access, isReady]
  );

  const can = useCallback(
    (permission: PermissionCode) => {
      if (isUngated) return true;
      return isReady ? hasPermission(permission, rolePermissions) : false;
    },
    [isReady, isUngated, rolePermissions]
  );

  const canAny = useCallback(
    (permissions: PermissionCode[]) => permissions.some(can),
    [can]
  );

  return { can, canAny, rolePermissions, isReady };
};

export const usePermission = (permission: PermissionCode): boolean =>
  useAccessControl().can(permission);

export default usePermission;
